import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Link2, Plus, Users } from 'lucide-react';
import LinkedClassGroupForm from '../components/LinkedClassGroupForm';

const LinkedClasses = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('class_link_groups')
      .select(`
        *,
        members:class_link_members(
          *,
          class:classes(id, name, start_time, end_time)
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    setGroups(data || []);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will not delete the classes, just unlink them.')) return;
    
    await supabase
      .from('class_link_groups')
      .update({ is_active: false })
      .eq('id', id);
    
    fetchGroups();
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Linked Classes</h1>
          <p className="text-gray-500">Manage related classes with extended hours or add-ons</p>
        </div>
        <button
          onClick={() => {
            setEditingGroup(null);
            setShowForm(true);
          }}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Create Linked Group</span>
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingGroup ? 'Edit Linked Class Group' : 'Create Linked Class Group'}
          </h3>
          <LinkedClassGroupForm
            group={editingGroup}
            onSave={() => {
              setShowForm(false);
              fetchGroups();
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Link2 size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No linked class groups yet</h3>
          <p className="text-gray-500 mb-4">Create a group to link related classes together</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
          >
            Create Your First Group
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {groups.map(group => (
            <div key={group.id} className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                  {group.description && (
                    <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingGroup(group);
                      setShowForm(true);
                    }}
                    className="text-purple-600 hover:text-purple-700 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {group.members?.map((member, idx) => (
                  <div key={idx} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                    <span className={`badge text-xs ${
                      member.link_type === 'primary' ? 'bg-purple-100 text-purple-800' :
                      member.link_type === 'extended' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {member.link_type}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{member.class?.name}</p>
                      <p className="text-xs text-gray-500">
                        {member.class?.start_time} - {member.class?.end_time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {group.capacity_limit && (
                <div className="mt-4 flex items-center text-sm text-gray-600">
                  <Users size={16} className="mr-2" />
                  Capacity limit: {group.capacity_limit} students
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkedClasses;
